import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Mail, Send, Sparkles } from 'lucide-react';
import { settingsApi } from '../../../api/endpoints/settings';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Toggle } from '../../../components/ui/Toggle';
import { useToast } from '../../../components/ui/Alert';
import { ColorPickerField } from '../../../components/email-editor/ColorPickerField';
import type { StoreSettings } from '../../../types/settings';

interface Props { settings?: StoreSettings; storeId: string; }

// Initial values for the pickers when nothing is saved yet — chosen to be a
// reasonable starting point if the merchant decides to customize. The actual
// "do nothing custom" rendering is governed by the `useZero` toggle below;
// when it's ON the layout falls back to the real Zero gradient (navy → black,
// black button) regardless of these values.
const PICKER_FALLBACK = {
  brandColor:      '#2F6FAC',
  buttonColor:     '#2F6FAC',
  backgroundColor: '#FAFAFA',
};

// What the storefront layout actually renders when no brand colors are saved.
// Matched against emails/store/_layout.ejs so the preview is honest.
const ZERO_BRAND = {
  heroBg:   'linear-gradient(180deg, #1E2B42 0%, #010101 100%)',
  buttonBg: '#000000',
  pageBg:   '#FAFAFA',
};

export function EmailBrandingTab({ settings, storeId }: Props) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const saved = settings?.emailBranding || {};
  const hasAnySaved = !!(saved.brandColor || saved.buttonColor || saved.backgroundColor);

  const [useZero, setUseZero] = useState<boolean>(!hasAnySaved);
  const [brandColor,      setBrandColor]      = useState(saved.brandColor      || PICKER_FALLBACK.brandColor);
  const [buttonColor,     setButtonColor]     = useState(saved.buttonColor     || PICKER_FALLBACK.buttonColor);
  const [backgroundColor, setBackgroundColor] = useState(saved.backgroundColor || PICKER_FALLBACK.backgroundColor);
  const [testEmail,       setTestEmail]       = useState('');

  // Re-sync when the settings query refetches (e.g. after save).
  useEffect(() => {
    const b = settings?.emailBranding;
    const customized = !!(b && (b.brandColor || b.buttonColor || b.backgroundColor));
    setUseZero(!customized);
    if (b) {
      setBrandColor(b.brandColor || PICKER_FALLBACK.brandColor);
      setButtonColor(b.buttonColor || PICKER_FALLBACK.buttonColor);
      setBackgroundColor(b.backgroundColor || PICKER_FALLBACK.backgroundColor);
    }
  }, [settings?.emailBranding]);

  const saveMutation = useMutation({
    mutationFn: () => settingsApi.updateEmailBranding(
      storeId,
      // Empty strings tell the backend to clear the brand → layout uses Zero.
      useZero
        ? { brandColor: '', buttonColor: '', backgroundColor: '' }
        : { brandColor, buttonColor, backgroundColor },
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', storeId] });
      toast('success', t('emailBranding.saved', 'Email branding saved'));
    },
    onError: () => toast('error', t('common.error')),
  });

  const testMutation = useMutation({
    mutationFn: () => settingsApi.sendEmailBrandingTest(storeId, testEmail || undefined),
    onSuccess: (data) => toast('success', t('emailBranding.testSent', 'Test email sent to {{email}}', { email: data.to })),
    onError: () => toast('error', t('emailBranding.testFailed', 'Failed to send test email')),
  });

  const previewHeroBg   = useZero ? ZERO_BRAND.heroBg   : `linear-gradient(135deg, ${brandColor}, ${brandColor}cc)`;
  const previewButtonBg = useZero ? ZERO_BRAND.buttonBg : `linear-gradient(135deg, ${buttonColor}, ${buttonColor}dd)`;
  const previewPageBg   = useZero ? ZERO_BRAND.pageBg   : backgroundColor;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <section className="rounded-xl border border-border-default bg-bg-secondary p-6 space-y-5">
        <div className="flex items-start gap-3">
          <Mail className="h-5 w-5 mt-0.5 text-accent" />
          <div>
            <h2 className="text-base font-semibold">
              {t('emailBranding.title', 'Email branding')}
            </h2>
            <p className="text-sm text-text-muted mt-1">
              {t(
                'emailBranding.description',
                'Colors used by every email your store sends to customers — order confirmations, status updates, cart recovery, account creation. Your store logo and name come from General settings.',
              )}
            </p>
          </div>
        </div>

        <div className="rounded-lg border border-border-default bg-bg-tertiary p-4 flex items-start gap-3">
          <Sparkles className="h-5 w-5 mt-0.5 text-accent shrink-0" />
          <Toggle
            label={t('emailBranding.useZero', 'Use Zero brand defaults')}
            description={t(
              'emailBranding.useZeroDesc',
              'Customer emails use the Zero look (navy → black header, black button). Turn off to set your own colors.',
            )}
            checked={useZero}
            onChange={setUseZero}
          />
        </div>

        {!useZero && (
          <div className="grid gap-4 sm:grid-cols-2">
            <ColorPickerField
              label={t('emailBranding.brandColor', 'Brand color (header background)')}
              value={brandColor}
              onChange={setBrandColor}
            />
            <ColorPickerField
              label={t('emailBranding.buttonColor', 'Button color')}
              value={buttonColor}
              onChange={setButtonColor}
            />
            <ColorPickerField
              label={t('emailBranding.backgroundColor', 'Background color')}
              value={backgroundColor}
              onChange={setBackgroundColor}
            />
          </div>
        )}

        {/* Live mini-preview — reflects what the layout will actually render */}
        <div
          className="rounded-xl overflow-hidden border border-border-default"
          style={{ background: previewPageBg }}
        >
          <div
            className="px-6 py-8 text-center text-white"
            style={{ background: previewHeroBg }}
          >
            <div className="mx-auto mb-3 h-14 w-14 rounded-full bg-white/15 border border-white/20 flex items-center justify-center text-2xl">
              {(settings?.name || 'Z').charAt(0)}
            </div>
            <div className="text-base font-bold">{settings?.name || 'Your store'}</div>
          </div>
          <div className="bg-white px-6 py-6 text-sm text-gray-700 text-center">
            {t('emailBranding.previewBody', 'Sample body text appears here in white card on your background.')}
            <div className="mt-4">
              <span
                className="inline-block px-7 py-3 rounded-xl text-white font-bold text-sm"
                style={{ background: previewButtonBg }}
              >
                {t('emailBranding.previewCta', 'Sample button')}
              </span>
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <Button onClick={() => saveMutation.mutate()} isLoading={saveMutation.isPending}>
            {t('common.save')}
          </Button>
        </div>
      </section>

      <section className="rounded-xl border border-border-default bg-bg-secondary p-6 space-y-4">
        <div>
          <h2 className="text-base font-semibold">
            {t('emailBranding.testTitle', 'Send a test email')}
          </h2>
          <p className="text-sm text-text-muted mt-1">
            {t(
              'emailBranding.testDescription',
              'Sends a sample order-confirmation rendered with your saved branding. Save your changes first.',
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[240px]">
            <Input
              label={t('emailBranding.testEmail', 'Recipient (defaults to your account email)')}
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="you@example.com"
              dir="ltr"
            />
          </div>
          <Button onClick={() => testMutation.mutate()} isLoading={testMutation.isPending}>
            <Send className="h-4 w-4 me-2" />
            {t('emailBranding.testSend', 'Send test')}
          </Button>
        </div>
      </section>
    </div>
  );
}
